import { inject, Injectable } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  HttpParams,
} from '@web/common';
import { map, Observable } from 'rxjs';
import { AdminWarehouseGoodCollectionItemResponse } from '../responses/admin-warehouse-good-collection-item.response';
import { Guid, valueOrDefault } from '@web/utils';
import { AdminWarehouseGoodDetailsResponse } from '../responses/admin-warehouse-good-details.response';
import { AdminWarehouseGoodUsageResponse } from '../responses/admin-warehouse-good-usage.response';

@Injectable({
  providedIn: 'root',
})
export class AdminWarehouseGoodApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  /* prettier-ignore */
  public fetchCollection(
    request: CollectionRequest,
    options?: {
      cacheable?: boolean;
      skipByLoading?: boolean;
    }
  ): Observable<CollectionResponse<AdminWarehouseGoodCollectionItemResponse>> {
    const path = 'admin/warehouse/good';
    const url = this.provider.httpUrlFactory.withQueryParams(this.hostUrl(), path, this.provider.httpHelper.collectionRequestToQueryParams(request));
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .withOptionalSkipByLoadingInterceptor(() =>
        valueOrDefault(options?.skipByLoading, false),
      )
      .withOptionalCacheInterceptor(() =>
        valueOrDefault(options?.cacheable, false),
      )
      .build();

    return this.provider.http.get(url, { context }).pipe(
      map(
        this.provider.httpHelper.bodyToCollectionResponse(
          AdminWarehouseGoodCollectionItemResponse,
        ),
      ),
    );
  }

  public fetchDetailsById(
    id: Guid,
    cacheable = false,
  ): Observable<AdminWarehouseGoodDetailsResponse> {
    const path = `admin/warehouse/good/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .withOptionalCacheInterceptor(() => cacheable)
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            AdminWarehouseGoodDetailsResponse,
          ),
        ),
      );
  }

  public fetchGoodUsage(id: Guid): Observable<AdminWarehouseGoodUsageResponse> {
    const path = `admin/warehouse/good/${HttpParams.Id}/usage`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            AdminWarehouseGoodUsageResponse,
          ),
        ),
      );
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Shop;
  }
}
